import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    globalDeferredPrompt = e as BeforeInstallPromptEvent;
    notifyListeners();
  });

  window.addEventListener("appinstalled", () => {
    globalDeferredPrompt = null;
    notifyListeners();
  });
}

function getIsInstalled(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  if ((navigator as any).standalone === true) return true;
  return false;
}

export function usePWAInstall() {
  const [, setTick] = useState(0);
  const [isInstalled, setIsInstalled] = useState(getIsInstalled);

  useEffect(() => {
    const rerender = () => setTick((t) => t + 1);
    listeners.add(rerender);

    const mq = window.matchMedia("(display-mode: standalone)");
    const handleChange = () => setIsInstalled(getIsInstalled());
    mq.addEventListener("change", handleChange);

    return () => {
      listeners.delete(rerender);
      mq.removeEventListener("change", handleChange);
    };
  }, []);

  const canInstall = globalDeferredPrompt !== null && !isInstalled;

  const install = useCallback(async () => {
    if (!globalDeferredPrompt) return false;
    globalDeferredPrompt.prompt();
    const { outcome } = await globalDeferredPrompt.userChoice;
    if (outcome === "accepted") {
      globalDeferredPrompt = null;
      setIsInstalled(true);
      notifyListeners();
      return true;
    }
    return false;
  }, []);

  return { canInstall, isInstalled, install };
}
